import { formatDate } from "@/utils/datetime";

/**
 * 학습 시작일 프리셋. "최근 N일" 기간이 현재 날짜를 기준으로 움직인다.
 *
 * URL 에는 프리셋 키(`started_within=7d`)로 남고, 요청 시점에 절대 날짜(`started_after`/
 * `started_before`, `YYYY-MM-DD` 양끝 포함)로 바꾼다. 절대 날짜를 URL 에 고정하면 공유한 링크가
 * 다음 날 "최근 1주"의 뜻을 잃는다. 정책 탭 `registered_within` 과 같은 선택이다. 변환은 보는 사람의
 * 로컬 자정 기준이다. 기간은 `오늘-(N-1)일 ~ 오늘` 이라서 "1주"는 오늘을 포함한 7개 날짜다. 라벨
 * 네 단어(전체·최근 1주·2주·한 달)는 정책 카탈로그 등록일(policyRegisteredWithin.ts)과 같다.
 */
export const SESSION_STARTED_WITHIN_OPTIONS = [
  { value: "7d", label: "최근 1주", days: 7 },
  { value: "14d", label: "최근 2주", days: 14 },
  { value: "30d", label: "최근 한 달", days: 30 },
] as const;

export type SessionStartedWithin = (typeof SESSION_STARTED_WITHIN_OPTIONS)[number]["value"];

// URL 값은 외부 입력이다. 정확한 어휘만 받는다.
export function isSessionStartedWithin(value: string): value is SessionStartedWithin {
  return SESSION_STARTED_WITHIN_OPTIONS.some((option) => option.value === value);
}

export function sessionStartedWithinLabel(value: SessionStartedWithin): string {
  return SESSION_STARTED_WITHIN_OPTIONS.find((option) => option.value === value)?.label ?? value;
}

/** 프리셋 → 절대 날짜 범위. `now` 는 테스트가 고정할 수 있도록 인자로 받는다. */
export function sessionStartedWithinRange(
  value: SessionStartedWithin,
  nowMs: number,
): { startedAfter: string; startedBefore: string } {
  const days = SESSION_STARTED_WITHIN_OPTIONS.find((option) => option.value === value)?.days ?? 0;
  const today = new Date(nowMs);
  // 로컬 자정으로 맞춘 뒤 날짜를 계산한다. DST 경계에서는 하루가 23·25시간이라 ms 를 빼면 날짜가
  // 틀릴 수 있다. 그래서 setDate 로 달력 날짜를 옮긴다.
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  start.setDate(start.getDate() - (days - 1));
  return { startedAfter: formatDate(start.getTime()), startedBefore: formatDate(nowMs) };
}
